import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { format, addDays } from 'date-fns';
import { it } from 'date-fns/locale';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const HOURS = Array.from({ length: 24 }, (_, i) => i);
const DOT = { blue: '#3b82f6', amber: '#f59e0b', emerald: '#10b981', red: '#ef4444', purple: '#a855f7' };
const pad = (n) => String(n).padStart(2, '0');

/**
 * iter103 — Sticker agenda del giorno (per finestra always-on-top / Picture-in-Picture
 * o finestra su secondo schermo). Mostra gli impegni di oggi per orario, si aggiorna
 * da solo ogni 60s. Sincronizzato con il calendario del gestionale.
 */
export default function AgendaStickerPage() {
  const [events, setEvents] = useState([]);
  const [onlyTodo, setOnlyTodo] = useState(() => localStorage.getItem('sticker_only_todo') === '1');
  const [reschedId, setReschedId] = useState(null);
  const today = new Date();
  const dayKey = format(today, 'yyyy-MM-dd');

  const toggleOnlyTodo = useCallback(() => {
    setOnlyTodo((v) => {
      const next = !v;
      localStorage.setItem('sticker_only_todo', next ? '1' : '0');
      return next;
    });
  }, []);

  const fetchEvents = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/dashboard/calendar-events`, {
        params: { date_from: dayKey, date_to: dayKey }, withCredentials: true,
      });
      setEvents((r.data?.items || []).filter((e) => String(e.dtstart || '').slice(0, 10) === dayKey));
    } catch { /* keep */ }
  }, [dayKey]);

  const toggleDone = useCallback(async (e) => {
    // Aggiornamento ottimistico così la spunta è immediata anche nello sticker
    setEvents((list) => list.map((x) => (x.id === e.id ? { ...x, done: !x.done } : x)));
    try {
      await axios.put(`${API}/dashboard/calendar-events/${e.id}`, { done: !e.done }, { withCredentials: true });
    } catch {
      setEvents((list) => list.map((x) => (x.id === e.id ? { ...x, done: e.done } : x)));
    }
  }, []);

  // Rinvia l'evento ad un'altra data mantenendo l'orario: se non è più oggi sparisce dallo sticker
  const reschedule = useCallback(async (e, newDate) => {
    if (!newDate) { setReschedId(null); return; }
    const payload = e.all_day
      ? { dtstart: newDate, dtend: newDate }
      : { dtstart: `${newDate}${String(e.dtstart).slice(10) || 'T09:00:00'}`, dtend: `${newDate}${String(e.dtend).slice(10) || 'T10:00:00'}` };
    setReschedId(null);
    setEvents((list) => list.map((x) => (x.id === e.id ? { ...x, dtstart: payload.dtstart, dtend: payload.dtend } : x)));
    try {
      await axios.put(`${API}/dashboard/calendar-events/${e.id}`, payload, { withCredentials: true });
      fetchEvents();
    } catch { fetchEvents(); }
  }, [fetchEvents]);

  // Pannello rinvio compatto: pulsante "Domani" + scelta data libera (input vuoto
  // così qualsiasi selezione scatena onChange e il rinvio parte sempre)
  const reschedPanel = (e) => (
    <div style={{ display: 'flex', gap: 6, alignItems: 'center', padding: '4px 6px' }}>
      <button onClick={() => reschedule(e, format(addDays(today, 1), 'yyyy-MM-dd'))} data-testid={`sticker-reschedule-tomorrow-${e.id}`}
        style={{ fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 6, cursor: 'pointer', border: '1px solid #475569', background: 'transparent', color: '#cbd5e1' }}>Domani</button>
      <input type="date" min={dayKey} onChange={(ev) => reschedule(e, ev.target.value)} data-testid={`sticker-reschedule-input-${e.id}`}
        style={{ fontSize: 11, background: '#0f172a', color: '#e2e8f0', border: '1px solid #475569', borderRadius: 4, padding: '2px 4px' }} />
    </div>
  );

  useEffect(() => {
    fetchEvents();
    const t = setInterval(fetchEvents, 60000);
    return () => clearInterval(t);
  }, [fetchEvents]);

  const visible = onlyTodo ? events.filter((e) => !e.done) : events;
  const allDay = visible.filter((e) => e.all_day);
  const timed = visible.filter((e) => !e.all_day).sort((a, b) => String(a.dtstart).localeCompare(String(b.dtstart)));
  const byHour = {};
  for (const e of timed) {
    const h = parseInt(String(e.dtstart).slice(11, 13), 10) || 0;
    (byHour[h] = byHour[h] || []).push(e);
  }
  const hoursWithEvents = HOURS.filter((h) => byHour[h]?.length);

  return (
    <div style={{ fontFamily: 'system-ui, sans-serif', background: '#0f172a', color: '#e2e8f0', minHeight: '100vh', padding: 0 }} data-testid="agenda-sticker">
      <div style={{ position: 'sticky', top: 0, background: '#1e293b', padding: '8px 12px', borderBottom: '1px solid #334155', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
        <div>
          <div style={{ fontSize: 13, fontWeight: 700 }}>Impegni di oggi</div>
          <div style={{ fontSize: 11, color: '#94a3b8', textTransform: 'capitalize' }}>{format(today, 'EEEE d MMMM', { locale: it })}</div>
        </div>
        <button onClick={toggleOnlyTodo} data-testid="sticker-filter-toggle"
          title={onlyTodo ? 'Mostra tutti gli impegni' : 'Mostra solo gli impegni da fare'}
          style={{ fontSize: 11, fontWeight: 600, padding: '4px 8px', borderRadius: 999, cursor: 'pointer', flexShrink: 0, border: '1px solid', borderColor: onlyTodo ? '#10b981' : '#475569', background: onlyTodo ? 'rgba(16,185,129,0.15)' : 'transparent', color: onlyTodo ? '#34d399' : '#94a3b8' }}>
          {onlyTodo ? 'Solo da fare' : 'Tutti'}
        </button>
      </div>
      <div style={{ padding: '8px 10px' }}>
        {allDay.map((e) => (
          <div key={e.id} style={{ marginBottom: 4 }}>
            <div style={{ display: 'flex', gap: 6, alignItems: 'center', padding: '4px 6px', background: '#1e293b', borderRadius: 6, opacity: e.done ? 0.55 : 1 }}>
              <button onClick={() => toggleDone(e)} data-testid={`sticker-done-${e.id}`} title={e.done ? 'Segna da fare' : 'Segna come svolto'}
                style={{ width: 18, height: 18, borderRadius: 4, border: '1px solid', borderColor: e.done ? '#10b981' : '#475569', background: e.done ? '#10b981' : 'transparent', color: '#fff', cursor: 'pointer', flexShrink: 0, lineHeight: '16px', fontSize: 12, padding: 0 }}>
                {e.done ? '✓' : ''}
              </button>
              <span style={{ width: 8, height: 8, borderRadius: 8, background: DOT[e.colore] || DOT.blue, flexShrink: 0 }} />
              <span style={{ fontSize: 12, textDecoration: e.done ? 'line-through' : 'none', flex: 1 }}>Tutto il giorno · {e.summary}</span>
              <button onClick={() => setReschedId(reschedId === e.id ? null : e.id)} data-testid={`sticker-reschedule-${e.id}`} title="Rinvia ad un altro giorno"
                style={{ background: 'transparent', border: 'none', color: reschedId === e.id ? '#34d399' : '#94a3b8', cursor: 'pointer', fontSize: 13, padding: '0 2px', flexShrink: 0 }}>↪</button>
            </div>
            {reschedId === e.id && reschedPanel(e)}
          </div>
        ))}
        {hoursWithEvents.length === 0 && allDay.length === 0 && (
          <div style={{ textAlign: 'center', color: '#64748b', fontSize: 12, padding: '24px 0' }}>
            {onlyTodo ? 'Nessun impegno da fare 👍' : 'Nessun impegno per oggi 🎉'}
          </div>
        )}
        {hoursWithEvents.map((h) => (
          <div key={h} style={{ display: 'flex', gap: 8, marginBottom: 6 }}>
            <div style={{ width: 38, fontSize: 11, color: '#64748b', paddingTop: 2 }}>{pad(h)}:00</div>
            <div style={{ flex: 1 }}>
              {byHour[h].map((e) => (
                <div key={e.id} style={{ marginBottom: 4 }}>
                  <div style={{ display: 'flex', gap: 6, alignItems: 'center', padding: '4px 6px', background: '#1e293b', borderRadius: 6, borderLeft: `3px solid ${DOT[e.colore] || DOT.blue}`, opacity: e.done ? 0.55 : 1 }}>
                    <button onClick={() => toggleDone(e)} data-testid={`sticker-done-${e.id}`} title={e.done ? 'Segna da fare' : 'Segna come svolto'}
                      style={{ width: 18, height: 18, borderRadius: 4, border: '1px solid', borderColor: e.done ? '#10b981' : '#475569', background: e.done ? '#10b981' : 'transparent', color: '#fff', cursor: 'pointer', flexShrink: 0, lineHeight: '16px', fontSize: 12, padding: 0 }}>
                      {e.done ? '✓' : ''}
                    </button>
                    <span style={{ fontSize: 11, fontWeight: 700, color: '#cbd5e1' }}>{String(e.dtstart).slice(11, 16)}</span>
                    <span style={{ fontSize: 12, textDecoration: e.done ? 'line-through' : 'none', flex: 1 }}>{e.summary}</span>
                    <button onClick={() => setReschedId(reschedId === e.id ? null : e.id)} data-testid={`sticker-reschedule-${e.id}`} title="Rinvia ad un altro giorno"
                      style={{ background: 'transparent', border: 'none', color: reschedId === e.id ? '#34d399' : '#94a3b8', cursor: 'pointer', fontSize: 13, padding: '0 2px', flexShrink: 0 }}>↪</button>
                  </div>
                  {reschedId === e.id && reschedPanel(e)}
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
